// EventPhotoGallery.js
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import Momentsia_logo from "../images/Momentsia _logo.png";

// Import Music Concert Images
import concert1 from "../images/concert1.jpg";
import concert2 from "../images/concert2.jpg";
import concert3 from "../images/concert3.jpg";
import concert4 from "../images/concert4.jpg";
import concert5 from "../images/concert5.JPG";
import concert6 from "../images/concert6.JPG";
import concert7 from "../images/concert7.JPG";

import emailjs from '@emailjs/browser';
import './EventPhotoGallery.css';

// ---------- LOGO COLOR PALETTE ----------
const LOGO_RED = "#E63946";
const LOGO_ORANGE = "#F4A261";
const LOGO_GREEN = "#8AC926";
const LOGO_TEAL = "#0ecfb0";
const LOGO_BLUE = "#457B9D";
const LOGO_MAGENTA = "#D81159";
const GOLD = "#d7ab77";

const T1 = LOGO_TEAL;
const T2 = LOGO_TEAL;
const T3 = "#077a6e";
const NAVY = "#0a0e23";
const NAVY_DEEP = "#080b1c";
const DARK = "#1a1a1a";

const BG_CUSTOM = "#f0fbf9"; 

const TEAL_GRAD = `linear-gradient(135deg, ${LOGO_TEAL}, ${LOGO_MAGENTA})`;

// EmailJS Configuration
const EMAILJS_SERVICE_ID = "service_zieecow";
const EMAILJS_TEMPLATE_ID = "template_l1xmaal";
const EMAILJS_PUBLIC_KEY = "q2O06yijgnOJTch1m";

const eventTypes = [
  "Corporate Event",
  "Wedding",
  "Birthday Party",
  "Conference",
  "Product Launch",
  "Music Concert",
  "Other",
];

// ---------- ANIMATED CONSTELLATION BACKGROUND ----------
const ParticleNetwork = ({ particleColor = GOLD, lineColor = "215,171,119", maxParticles = 130 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    let width = 0, height = 0, particles = [], animationId = null;
    const prefersReducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const buildParticles = () => {
      const area = width * height;
      const count = Math.min(Math.floor(area / 9000), maxParticles);
      particles = Array.from({ length: Math.max(count, 24) }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        r: Math.random() * 1.3 + 0.6,
      }));
    };

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildParticles();
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach(p => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x <= 0 || p.x >= width) p.vx *= -1;
        if (p.y <= 0 || p.y >= height) p.vy *= -1;
      });
      const maxDist = 125;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j];
          const dx = a.x - b.x, dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDist) {
            ctx.strokeStyle = `rgba(${lineColor},${0.16 * (1 - dist / maxDist)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.globalAlpha = 0.85;
        ctx.fill();
        ctx.globalAlpha = 1;
      });
      if (!prefersReducedMotion) animationId = requestAnimationFrame(step);
    };

    resize();
    step();
    window.addEventListener("resize", resize);
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, [particleColor, lineColor, maxParticles]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block", pointerEvents: "none" }}
    />
  );
};

const MomentsiaLogo = () => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
    <img src={Momentsia_logo} alt="Momentsia" style={{ height: 48, width: "auto", objectFit: "contain" }} />
  </div>
);

// ---------- CONSULT DIALOG ----------
const ConsultDialog = ({ open, onClose }) => {
  const [dialogFormData, setDialogFormData] = useState({
    name: "",
    phone: "",
    email: "",
    eventType: "",
    date: "",
    guests: "",
    message: ""
  });
  const [dialogErrors, setDialogErrors] = useState({});
  const [dialogStatus, setDialogStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateDialogForm = () => {
    const newErrors = {};
    if (!dialogFormData.name?.trim()) newErrors.name = "Name is required";
    if (!dialogFormData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(dialogFormData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!dialogFormData.phone?.trim()) newErrors.phone = "Phone number is required";
    if (!dialogFormData.eventType) newErrors.eventType = "Event type is required";
    if (!dialogFormData.date) newErrors.date = "Date is required";
    setDialogErrors(newErrors);
    return newErrors;
  };

  const handleDialogChange = (e) => {
    const { name, value } = e.target;
    setDialogFormData({ ...dialogFormData, [name]: value });
    if (dialogErrors[name]) {
      setDialogErrors({ ...dialogErrors, [name]: "" });
    }
  };

  const handleDialogSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateDialogForm();
    if (Object.keys(validationErrors).length > 0) return;

    setIsSubmitting(true);
    setDialogStatus("Sending...");

    const templateParams = {
      from_name: dialogFormData.name.trim(),
      from_email: dialogFormData.email.trim(),
      from_phone: dialogFormData.phone?.trim() || "Not provided",
      event_type: dialogFormData.eventType,
      event_date: dialogFormData.date,
      guests: dialogFormData.guests?.toString().trim() || "Not specified",
      message: dialogFormData.message?.trim() || "No additional message provided",
      submitted_at: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    try {
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      console.log("Dialog - Email sent successfully!", response);
      setDialogStatus("✓ Request sent successfully! We'll contact you within 24 hours.");
      setDialogFormData({ name: "", phone: "", email: "", eventType: "", date: "", guests: "", message: "" });
      setDialogErrors({});

      setTimeout(() => {
        setDialogStatus("");
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Dialog - Failed to send email:", error);
      setDialogStatus("❌ Failed to send. Please try again or call us directly.");
      setTimeout(() => setDialogStatus(""), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.6)" }} />
      <div style={{ position: "relative", background: "#fff", borderRadius: 14, padding: "40px 36px", maxWidth: 520, width: "90%", boxShadow: "0 24px 64px rgba(0,0,0,0.25)", maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 14, right: 18, background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#aaa" }}>✕</button>
        <div style={{ height: 4, background: TEAL_GRAD, borderRadius: 4, marginBottom: 24 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <img src={Momentsia_logo} alt="Momentsia" style={{ height: 32, width: "auto" }} />
          <h3 style={{ margin: 0, color: DARK, fontWeight: 800, fontSize: 20 }}>Plan Your Perfect Event</h3>
        </div>
        <p style={{ color: "#888", fontSize: 13, margin: "0 0 22px" }}>Share your event details – we'll create a customized proposal within 24 hours.</p>

        <form onSubmit={handleDialogSubmit}>
          <input
            type="text"
            name="name"
            placeholder="Full Name *"
            value={dialogFormData.name}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.name ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.name && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.name}</p>}

          <input
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={dialogFormData.phone}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.phone ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.phone && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.phone}</p>}

          <input
            type="email"
            name="email"
            placeholder="Email Address *"
            value={dialogFormData.email}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.email ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, boxSizing: "border-box" }}
          />
          {dialogErrors.email && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.email}</p>}

          <select
            name="eventType"
            value={dialogFormData.eventType}
            onChange={handleDialogChange}
            required
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.eventType ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14, background: "#fff" }}
          >
            <option value="">Select Event Type *</option>
            {eventTypes.map(type => <option key={type} value={type}>{type}</option>)}
          </select>
          {dialogErrors.eventType && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.eventType}</p>}

          <input
            type="date"
            name="date"
            value={dialogFormData.date}
            onChange={handleDialogChange}
            required
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: `1.5px solid ${dialogErrors.date ? "#f44336" : "#e0e0e0"}`, borderRadius: 7, fontSize: 14 }}
          />
          {dialogErrors.date && <p style={{ color: "#f44336", fontSize: 11, margin: "-8px 0 12px 0" }}>{dialogErrors.date}</p>}

          <input
            type="number"
            name="guests"
            placeholder="Approximate Guests"
            value={dialogFormData.guests}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 13, padding: "11px 14px", border: "1.5px solid #e0e0e0", borderRadius: 7, fontSize: 14 }}
          />

          <textarea
            name="message"
            placeholder="Tell us about your event (themes, requirements, ideas)..."
            rows={3}
            value={dialogFormData.message}
            onChange={handleDialogChange}
            style={{ display: "block", width: "100%", marginBottom: 18, padding: "11px 14px", border: "1.5px solid #e0e0e0", borderRadius: 7, fontSize: 14, resize: "vertical" }}
          />

          <button
            type="submit"
            disabled={isSubmitting}
            style={{ width: "100%", padding: "13px 0", background: TEAL_GRAD, color: "#fff", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 15, cursor: isSubmitting ? "not-allowed" : "pointer", opacity: isSubmitting ? 0.7 : 1 }}
          >
            {isSubmitting ? "Submitting..." : "Submit Inquiry"}
          </button>
          {dialogStatus && <p style={{ marginTop: 16, fontSize: 12, textAlign: "center", color: dialogStatus.includes("✓") ? T2 : T1 }}>{dialogStatus}</p>}
        </form>
      </div>
    </div>
  );
};

// Gallery items - ONE Music Concert with 7 images
const galleryItems = [
  { id: 1, title: 'Music Concert - Performance', image: concert1 },
  { id: 2, title: 'Music Concert - Crowd', image: concert2 },
  { id: 3, title: 'Music Concert - Band', image: concert3 },
  { id: 4, title: 'Music Concert - Solo', image: concert4 },
  { id: 5, title: 'Music Concert - Drums', image: concert5 },
  { id: 6, title: 'Music Concert - Vocals', image: concert6 },
  { id: 7, title: 'Music Concert - Finale', image: concert7 },
  { id: 8, title: 'Corporate Annual Day', image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800&h=600&fit=crop' },
  { id: 9, title: 'Product Launch Event', image: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=600&fit=crop' },
  { id: 10, title: 'Award Function', image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&h=600&fit=crop' },
  { id: 11, title: 'Dealer Meet & Conference', image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop' },
  { id: 12, title: 'Team Outing & Family Day', image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&h=600&fit=crop' },
  { id: 13, title: 'Factory Launch Event', image: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&h=600&fit=crop' },
  { id: 14, title: 'Wedding Celebration', image: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=800&h=600&fit=crop' },
  { id: 15, title: 'Cultural Festival', image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=800&h=600&fit=crop' },
];

const EventPhotoGallery = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [openForm, setOpenForm] = useState(false);

  const openLightbox = (index) => setActiveIndex(index);
  const closeLightbox = () => setActiveIndex(null);

  // Navigate to next image
  const nextImage = (e) => {
    e.stopPropagation();
    if (activeIndex !== null && activeIndex < galleryItems.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  // Navigate to previous image
  const prevImage = (e) => {
    e.stopPropagation();
    if (activeIndex !== null && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeIndex !== null) {
        if (e.key === 'ArrowRight') {
          if (activeIndex < galleryItems.length - 1) {
            setActiveIndex(activeIndex + 1);
          }
        } else if (e.key === 'ArrowLeft') {
          if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
          }
        } else if (e.key === 'Escape') {
          closeLightbox();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex]);

  const currentItem = activeIndex !== null ? galleryItems[activeIndex] : null;
  const totalItems = galleryItems.length;

  return (
    <div className="epg-page">
      <link href="https://fonts.googleapis.com/css2?family=Allura&display=swap" rel="stylesheet" />
      <style>{`
        *{box-sizing:border-box;}body{margin:0;}
        h1, h2 {
          font-family: 'Allura', cursive !important;
          letter-spacing: 1px;
        }
        h3, h4, h5, h6, p, a, li, button, input, textarea {
          font-family: 'Segoe UI', Arial, sans-serif;
        }
        .cb:hover{opacity:0.88;transform:translateY(-1px);}

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(26px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { box-shadow: 0 4px 14px rgba(14,207,176,0.35); }
          50% { box-shadow: 0 6px 30px rgba(14,207,176,0.7); }
        }
        @keyframes floatOrb{0%,100%{transform:translateY(0) translateX(0);}50%{transform:translateY(-22px) translateX(14px);}}
        @keyframes pulseOrb{0%,100%{opacity:0.7;transform:scale(1);}50%{opacity:1;transform:scale(1.12);}}

        .epg-page{
          background: ${BG_CUSTOM};
          min-height: 100vh;
          color: ${DARK};
        }
        .epg-grid-section{ background: transparent !important; }
        .epg-grid-title{ color: ${DARK} !important; }
        .epg-grid-subtitle{ color: #555 !important; }
        .epg-cta{ background: transparent !important; }
        .epg-cta-content h2{ color: ${DARK} !important; }
        .epg-cta-content p{ color: #444 !important; }
        .epg-lightbox-title{ color: #fff !important; }

        .epg-hero{
          position: relative;
          overflow: hidden;
          background: transparent;
          padding: 96px 24px 120px;
          text-align: center;
        }
        .epg-hero-badge{
          display: inline-block;
          padding: 7px 18px;
          border-radius: 999px;
          background: rgba(0,0,0,0.05);
          border: 1px solid rgba(0,0,0,0.1);
          color: ${DARK};
          font-size: 12.5px;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          animation: fadeInUp .7s ease both;
        }
        .epg-hero-title{
          color: ${DARK};
          font-size: clamp(38px, 6vw, 66px);
          margin: 18px 0 14px;
          animation: fadeInUp .8s .12s ease both;
          text-shadow: none;
        }
        .epg-hero-sub{
          color: #555;
          font-size: 16px;
          max-width: 560px;
          margin: 0 auto;
          animation: fadeInUp .8s .24s ease both;
        }
        .epg-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 26px;
          max-width: 1200px;
          margin: 44px auto 0;
          padding: 0 24px 60px;
        }
        .epg-grid-header {
          text-align: center;
          padding: 20px 24px 10px;
        }
        .epg-highlight {
          color: ${LOGO_TEAL};
          text-shadow: none;
        }
        .epg-card{
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          padding: 0;
          cursor: pointer;
          text-align: left;
          animation: fadeInUp .6s ease both;
          transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 4px 16px rgba(0,0,0,0.05);
        }
        .epg-card:hover{
          transform: translateY(-6px);
          box-shadow: 0 18px 40px rgba(0,0,0,0.15);
          border-color: rgba(0,0,0,0.2);
        }
        .epg-card-image-wrap {
          position: relative;
          overflow: hidden;
          border-radius: 16px;
        }
        .epg-card-image-wrap img {
          width: 100%;
          height: 260px;
          object-fit: cover;
          display: block;
          transition: transform 0.5s ease;
        }
        .epg-card:hover .epg-card-image-wrap img {
          transform: scale(1.06);
        }
        .epg-card-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .epg-card:hover .epg-card-overlay {
          opacity: 1;
        }
        .epg-card-overlay-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #fff;
        }
        .epg-card-overlay-icon {
          font-size: 28px;
        }
        .epg-card-overlay-text {
          font-size: 14px;
          font-weight: 600;
          letter-spacing: 1px;
        }
        .epg-card-footer {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          padding: 36px 18px 16px;
          background: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 40%, rgba(0,0,0,0.7) 100%);
        }
        .epg-card-title {
          color: #fff;
          font-size: 15px;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 1px 4px rgba(0,0,0,0.5);
        }
        .epg-card-arrow {
          color: #fff;
          font-size: 20px;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        .epg-card:hover .epg-card-arrow {
          transform: translateX(4px);
        }

        /* Lightbox Styles with Previous/Next */
        .epg-lightbox {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.92);
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          animation: fadeInUp 0.3s ease;
        }
        .epg-lightbox-close {
          position: absolute;
          top: 24px;
          right: 30px;
          background: none;
          border: none;
          color: #fff;
          font-size: 40px;
          cursor: pointer;
          opacity: 0.7;
          transition: opacity 0.3s ease;
          z-index: 10;
        }
        .epg-lightbox-close:hover {
          opacity: 1;
        }
        .epg-lightbox-content {
          max-width: 900px;
          width: 100%;
          text-align: center;
          position: relative;
        }
        .epg-lightbox-content img {
          width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 12px;
        }
        .epg-lightbox-title {
          color: #fff;
          font-size: 20px;
          margin-top: 16px;
          font-weight: 600;
        }
        .epg-lightbox-counter {
          color: rgba(255,255,255,0.5);
          font-size: 14px;
          margin-top: 8px;
        }

        /* Navigation Buttons */
        .epg-lightbox-nav {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.2);
          color: #fff;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          font-size: 24px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          z-index: 10;
          backdrop-filter: blur(10px);
        }
        .epg-lightbox-nav:hover {
          background: rgba(255,255,255,0.25);
          border-color: rgba(255,255,255,0.4);
          transform: translateY(-50%) scale(1.05);
        }
        .epg-lightbox-nav:disabled {
          opacity: 0.3;
          cursor: not-allowed;
          transform: translateY(-50%) scale(1);
        }
        .epg-lightbox-prev {
          left: 20px;
        }
        .epg-lightbox-next {
          right: 20px;
        }

        .epg-cta {
          position: relative;
          overflow: hidden;
          padding: 80px 24px;
          background: transparent;
        }
        .epg-cta-inner {
          position: relative;
          z-index: 1;
          max-width: 800px;
          margin: 0 auto;
          background: rgba(255,255,255,0.95);
          border-radius: 24px;
          padding: 50px 40px;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
        }
        .epg-cta-content {
          text-align: center;
        }
        .epg-cta-content h2 {
          font-size: clamp(28px, 3.5vw, 40px);
          margin-bottom: 16px;
          color: ${DARK};
        }
        .epg-cta-content p {
          font-size: 16px;
          margin-bottom: 28px;
          line-height: 1.6;
          color: #555;
        }
        .epg-cta-btn {
          background: ${TEAL_GRAD};
          color: #fff;
          border: none;
          padding: 14px 42px;
          border-radius: 40px;
          font-weight: 700;
          font-size: 16px;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.3s ease;
          animation: pulseGlow 2.6s ease-in-out infinite;
        }
        .epg-cta-btn:hover {
          transform: scale(1.03);
          background: ${LOGO_TEAL};
        }
        .cb, .epg-cta-btn{
          animation: pulseGlow 2.6s ease-in-out infinite;
        }

        @media(max-width: 768px) {
          .epg-grid {
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 16px;
            padding: 16px;
          }
          .epg-card-image-wrap img {
            height: 180px;
          }
          .epg-hero {
            padding: 60px 16px 80px;
          }
          .epg-cta-inner {
            padding: 30px 20px;
          }
          .epg-lightbox-nav {
            width: 40px;
            height: 40px;
            font-size: 18px;
          }
          .epg-lightbox-prev {
            left: 8px;
          }
          .epg-lightbox-next {
            right: 8px;
          }
        }
        @media(max-width: 480px) {
          .epg-grid {
            grid-template-columns: 1fr;
          }
          .epg-card-image-wrap img {
            height: 200px;
          }
          .epg-lightbox-nav {
            width: 34px;
            height: 34px;
            font-size: 14px;
          }
        }
      `}</style>

      {/* Navigation Component */}
      <Navigation onPlanEventClick={() => setOpenForm(true)} />

      {/* HERO */}
      <section className="epg-hero" style={{ position: "relative", overflow: "hidden" }}>
        <ParticleNetwork maxParticles={110} particleColor={GOLD} lineColor="215,171,119" />
        <div style={{ position: "absolute", top: "-10%", right: "-6%", width: 320, height: 320, borderRadius: "50%", background: `radial-gradient(circle, rgba(14,207,176,0.05), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 11s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", bottom: "-14%", left: "-6%", width: 260, height: 260, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.05), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 13s ease-in-out infinite reverse", pointerEvents: "none", zIndex: 0 }} />

        <div style={{ position: "relative", zIndex: 2, maxWidth: 760, margin: "0 auto" }}>
          <span className="epg-hero-badge">Event Gallery</span>
          <h1 className="epg-hero-title">Moments We've <span className="epg-highlight">Created</span></h1>
          <p className="epg-hero-sub">A glimpse into the celebrations, launches, and concerts we've brought to life for our clients.</p>
        </div>

        <div className="epg-grid" style={{ position: "relative", zIndex: 2 }}>
          {galleryItems.map((item, idx) => (
            <button
              key={item.id}
              className="epg-card"
              style={{ animationDelay: `${idx * 0.07}s` }}
              onClick={() => openLightbox(idx)}
              aria-label={`View ${item.title}`}
            >
              <div className="epg-card-image-wrap">
                <img src={item.image} alt={item.title} loading="lazy" />
                <div className="epg-card-overlay">
                  <div className="epg-card-overlay-content">
                    <span className="epg-card-overlay-icon">🔍</span>
                    <span className="epg-card-overlay-text">View Gallery</span>
                  </div>
                </div>
                <div className="epg-card-footer">
                  <h4 className="epg-card-title">{item.title}</h4>
                  <span className="epg-card-arrow">→</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Lightbox with Previous/Next Navigation */}
      {activeIndex !== null && currentItem && (
        <div className="epg-lightbox" onClick={closeLightbox}>
          <button className="epg-lightbox-close" onClick={closeLightbox} aria-label="Close">
            &times;
          </button>

          {/* Previous Button */}
          <button
            className="epg-lightbox-nav epg-lightbox-prev"
            onClick={prevImage}
            disabled={activeIndex === 0}
            aria-label="Previous image"
          >
            ‹
          </button>

          <div className="epg-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={currentItem.image} alt={currentItem.title} />
            <p className="epg-lightbox-title">{currentItem.title}</p>
            <p className="epg-lightbox-counter">
              {activeIndex + 1} / {totalItems}
            </p>
          </div>

          {/* Next Button */}
          <button
            className="epg-lightbox-nav epg-lightbox-next"
            onClick={nextImage}
            disabled={activeIndex === totalItems - 1}
            aria-label="Next image"
          >
            ›
          </button>
        </div>
      )}

      {/* CTA Section */}
      <section className="epg-cta">
        <ParticleNetwork maxParticles={70} particleColor={GOLD} lineColor="215,171,119" />
        <div style={{ position: "absolute", top: "-12%", right: "-6%", width: 340, height: 340, borderRadius: "50%", background: `radial-gradient(circle, rgba(14,207,176,0.06), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 10s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", bottom: "-16%", left: "-6%", width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, rgba(215,171,119,0.05), transparent 70%)`, filter: "blur(30px)", animation: "floatOrb 12s ease-in-out infinite reverse", pointerEvents: "none", zIndex: 0 }} />
        <div style={{ position: "absolute", top: "38%", left: "50%", width: 220, height: 220, borderRadius: "50%", background: `radial-gradient(circle, rgba(14,207,176,0.04), transparent 70%)`, filter: "blur(28px)", animation: "pulseOrb 7s ease-in-out infinite", pointerEvents: "none", zIndex: 0 }} />

        <div className="epg-cta-inner">
          <div className="epg-cta-content">
            <h2>Ready to Create Your Own <span className="epg-highlight">Moments</span>?</h2>
            <p>Let us help you plan and execute your perfect event. From concept to completion, we handle everything.</p>
            <button className="epg-cta-btn" onClick={() => setOpenForm(true)}>
              Plan Your Event Now
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <Footer />

      {/* WhatsApp float */}
      <a href="https://wa.me/918100364941" target="_blank" rel="noreferrer" style={{ position: "fixed", bottom: 24, right: 24, width: 58, height: 58, borderRadius: "50%", background: "#25d366", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 20px rgba(37,211,102,0.4)", zIndex: 9998, textDecoration: "none", fontSize: 28 }}>💬</a>

      <ConsultDialog open={openForm} onClose={() => setOpenForm(false)} />
    </div>
  );
};

export default EventPhotoGallery;